import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import {
  useHandlers,
  useUser,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useSyncApi } from '@sparklayer-io/spark-dash-pages/features/sync/hooks/use-sync-api';
import { useSyncStatus } from '@sparklayer-io/spark-dash-pages/features/sync/hooks/use-sync-log-status';
import type { SyncLogIdentifier } from '@sparklayer-io/spark-dash-pages/features/sync/types';
import { Heading, LearnMoreLink, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { CustomerLookupSearch } from '../components/customer-lookup-search';
import { CustomersSyncLogAlert } from '../components/customers-sync-log-alert';
import { CUSTOMERS_SYNC_PATH, DOCS_URLS, I18N_PAGE } from '../constants';

type CustomersSyncPageProps = {
  /** Pre-fills and runs the lookup on mount, so a shared `?id=` link jumps straight to a result. */
  initialSearchTerm?: string;
};

export const CustomersSyncPage = withPageProviders(
  ({ initialSearchTerm }: CustomersSyncPageProps) => {
    const env = useEnv();
    const { isSparkStaff } = useUser();
    const { navigationHandler, notificationHandler } = useHandlers();
    const { syncApi } = useSyncApi();
    const [isSyncing, setIsSyncing] = useState<boolean>(false);

    const syncFor: SyncLogIdentifier = { integration: env.platform ?? '', dataType: 'customers' };
    // Same query key as CustomersSyncLogAlert, so refetching here updates the banner it renders.
    const { refetch: refetchSyncStatus, isFetching: isRefetchingStatus } = useSyncStatus(syncFor);

    const triggerManualSync = () => {
      setIsSyncing(true);
      syncApi
        .triggerManualSync({
          teamSlug: env.team.slugged_name,
          environment: env.environment,
          dataType: syncFor.dataType,
        })
        .then(() => {
          notificationHandler.success(t(`${I18N_PAGE}.notifications.sync_started`));
          return refetchSyncStatus();
        })
        .catch(() => notificationHandler.error())
        .finally(() => setIsSyncing(false));
    };

    return (
      <Page
        title={t(`${I18N_PAGE}.title`)}
        description={t(`${I18N_PAGE}.description`)}
        learnMoreUrl={DOCS_URLS.dataSync}
        primaryAction={
          isSparkStaff
            ? {
                label: t(`${I18N_PAGE}.actions.manual_sync`),
                onClick: triggerManualSync,
                loading: isSyncing,
              }
            : undefined
        }
        secondaryActions={[
          {
            label: t('buttons:reload'),
            onClick: () => void refetchSyncStatus(),
            loading: isRefetchingStatus,
          },
        ]}
      >
        <CustomersSyncLogAlert />

        <div className="flex flex-col gap-1">
          <Heading style="md" level="h2">
            {t(`${I18N_PAGE}.lookup_title`)}
          </Heading>
          <Text variant="secondary">
            {t(`${I18N_PAGE}.lookup_description`)} <LearnMoreLink link={DOCS_URLS.dataSyncLookup} />
          </Text>
        </div>

        <CustomerLookupSearch
          initialSearchTerm={initialSearchTerm}
          onSelect={(_customer, identifier) =>
            navigationHandler.navigate(`${CUSTOMERS_SYNC_PATH}/${encodeURIComponent(identifier)}`, {
              newTab: true,
            })
          }
        />
      </Page>
    );
  },
);
